'use client';

import { Search, X } from 'lucide-react';
import { FC, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { useTranslations } from 'next-intl';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle
} from '@components/ui/drawer';
import { cn } from '@utils/cn';
import type { KnowledgeSearchStatus } from '@hooks/use-knowledge-search';
import type { SearchHit } from '../../search/types';
import KnowledgeSearchAnswerCard from './KnowledgeSearchAnswerCard';
import type { KnowledgeSearchRowData } from './KnowledgeSearchRow';
import KnowledgeSearchSkeletonRow from './KnowledgeSearchSkeletonRow';

type KnowledgeSearchMobileProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationName: string | null | undefined;
  term: string;
  onTermChange: (term: string) => void;
  onSubmit: () => void;
  rows: KnowledgeSearchRowData[];
  emptyMessage: string | null;
  loadingLabel: string;
  defaultLabel: string | null;
  discovery: ReactNode;
  inputRef: RefObject<HTMLInputElement | null>;
  status: KnowledgeSearchStatus;
  onRetry: () => void;

  answer: { text: string; citedHitIds: string[] } | null;
  results: SearchHit[];
  onCitationClick: (hitId: string) => void;
  onAskAboutThis?: () => void;
};

/**
 * Full-screen mobile knowledge search. Same Outcome hierarchy as the
 * desktop dialog (eyebrow, heading, large field, rows), adapted to a
 * full-width small screen with the header/input pinned while results
 * scroll -- matches the previous SearchDrawer's chrome (close circle, pill
 * field) so the surrounding shell doesn't feel like two different apps.
 */
const KnowledgeSearchMobile: FC<KnowledgeSearchMobileProps> = ({
  open,
  onOpenChange,
  organizationName,
  term,
  onTermChange,
  onSubmit,
  rows,
  emptyMessage,
  loadingLabel,
  defaultLabel,
  status,
  onRetry,
  discovery,
  inputRef,
  answer,
  results,
  onCitationClick,
  onAskAboutThis
}) => {
  const t = useTranslations('knowledge-search');
  const common = useTranslations('common');
  const closeRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!open || !viewport) return;
    const update = () => {
      // Size only this panel; never scroll the document or fight pinch zoom.
      if (viewport.scale !== 1) return;
      contentRef.current?.style.setProperty(
        '--search-viewport-height',
        `${Math.min(window.innerHeight, viewport.height + viewport.offsetTop)}px`
      );
    };
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    update();
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [open]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
      autoFocus
      fixed
      repositionInputs={false}
    >
      <DrawerContent
        ref={contentRef}
        hideHandle
        onOpenAutoFocus={event => {
          event.preventDefault();
          closeRef.current?.focus({ preventScroll: true });
        }}
        aria-describedby={undefined}
        className="inset-x-0 top-0 bottom-auto mt-0 h-[var(--search-viewport-height,100dvh)] max-h-dvh overflow-hidden rounded-none border-0 bg-background pt-[var(--safe-top)] pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)]"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-4 pt-4">
            <div className="min-w-0">
              {organizationName ? (
                <div className="truncate text-column uppercase tracking-wider text-muted-foreground">
                  {organizationName}
                </div>
              ) : null}
              <DrawerTitle className="text-card-title">
                {t('heading')}
              </DrawerTitle>
            </div>
            <DrawerClose
              ref={closeRef}
              data-knowledge-search-mobile-close=""
              aria-label={common('close')}
              className="flex size-11 shrink-0 items-center justify-center rounded-full bg-card"
            >
              <X className="size-5" />
            </DrawerClose>
          </div>

          <div className="shrink-0 px-5 pb-4">
            <div className="knowledge-search-field flex min-h-12 items-center gap-2 rounded-full bg-card pl-4 pr-1">
              <Search
                className="size-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="search"
                inputMode="search"
                value={term}
                onChange={event => onTermChange(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.nativeEvent.isComposing)
                    onSubmit();
                }}
                placeholder={t('placeholder')}
                className="min-w-0 flex-1 border-0 bg-transparent px-0 py-3 text-body outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:appearance-none"
                aria-label={t('placeholder')}
              />
              {term ? (
                <button
                  type="button"
                  onClick={() => {
                    onTermChange('');
                    closeRef.current?.focus({ preventScroll: true });
                  }}
                  aria-label={t('clearSearch')}
                  className="flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
                >
                  <X className="size-4" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-5">
            {discovery}
            {defaultLabel ? (
              <p className="px-5 pb-2 text-caption text-muted-foreground">
                {defaultLabel}
              </p>
            ) : null}

            {answer ? (
              <KnowledgeSearchAnswerCard
                answerText={answer.text}
                citedHitIds={answer.citedHitIds}
                results={results}
                onCitationClick={onCitationClick}
                onAskAboutThis={onAskAboutThis}
              />
            ) : null}

            <div aria-live="polite">
              {status === 'loading' ? (
                <>
                  <p className="px-4 pb-2 pt-1 text-caption text-muted-foreground">
                    {loadingLabel}
                  </p>
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                  <KnowledgeSearchSkeletonRow />
                </>
              ) : emptyMessage ? (
                status === 'error' || status === 'offline' ? (
                  <div
                    role="alert"
                    className="flex flex-col items-center gap-3 px-6 py-12 text-center"
                  >
                    <p className="text-body text-muted-foreground">
                      {emptyMessage}
                    </p>
                    <button
                      type="button"
                      onClick={onRetry}
                      className="min-h-11 rounded-md border border-border px-4 text-label"
                    >
                      {t('retry')}
                    </button>
                  </div>
                ) : (
                  <p className="px-6 py-12 text-center text-body text-muted-foreground">
                    {emptyMessage}
                  </p>
                )
              ) : (
                <ul>
                  {rows.map(row => (
                    <li key={row.key}>
                      <button
                        type="button"
                        onClick={row.onSelect}
                        data-hit-id={row.hitId}
                        className={cn(
                          'flex w-full items-center gap-4 border-b border-border px-4 py-4 text-left transition-colors',
                          row.highlighted && 'bg-accent'
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-column uppercase tracking-wider text-muted-foreground">
                            {row.typeLabel}
                          </div>
                          <div className="mt-1 truncate text-card-title text-foreground">
                            {row.title}
                          </div>
                          {row.subtitle ? (
                            <div className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">
                              {row.subtitle}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default KnowledgeSearchMobile;
